import { useEffect, useState } from 'react'
import { useAppAuth } from '@/contexts/app-auth.context'
import { PageHeader } from '@/components/layout/PageHeader'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Button } from '@/components/ui/button'
import { toast } from 'sonner'
import { Skeleton } from '@/components/ui/skeleton'
import {
  TimezoneSelector,
  type TimezoneEnumValue,
} from '@/components/common/TimezoneSelector'
import { WeeklyHoursSchedule } from '@/components/common/WeeklyHoursSchedule'
import {
  DEFAULT_WEEKLY_SCHEDULE,
  DEFAULT_TIMEZONE,
  type WeeklySchedule,
} from '@super/shared-types'

export function CompanySettingsPage() {
  const {
    currentCompany,
    updateCompany,
    isCompanyLoading,
    isAuthLoading,
    isInitialized,
  } = useAppAuth()
  const [company, setCompany] = useState<{
    name: string
    timezoneName?: TimezoneEnumValue
    officeHours?: WeeklySchedule
  } | null>(null)
  const [saving, setSaving] = useState(false)

  useEffect(() => {
    // Clear company state when loading starts
    if (isCompanyLoading) {
      setCompany(null)
      return
    }

    if (currentCompany) {
      setCompany({
        name: currentCompany.name,
        timezoneName: (currentCompany.timezoneName ||
          DEFAULT_TIMEZONE) as TimezoneEnumValue,
        officeHours:
          currentCompany.officeHours?.weeklySchedule || DEFAULT_WEEKLY_SCHEDULE,
      })
    } else {
      setCompany(null)
    }
  }, [currentCompany, isCompanyLoading])

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault()
    e.stopPropagation()
    if (!company || !currentCompany?.id) return

    setSaving(true)
    try {
      // Filter out undefined values to avoid sending them to the API
      const updateData = Object.entries(company).reduce((acc, [key, value]) => {
        if (value !== undefined) {
          acc[key] = value
        }
        return acc
      }, {} as any)

      await updateCompany(currentCompany.id, updateData)
      toast.success(
        `Company details updated successfully for ${updateData.name}`,
      )
    } catch (error) {
      console.error('Error updating company:', error)
      toast.error('Failed to update company details')
    } finally {
      setSaving(false)
    }
  }

  // Show nothing while initial auth is loading (let the auth overlay handle this)
  if (isAuthLoading || !isInitialized) {
    return null
  }

  const pageHeaderTitle = 'Manage your company details and preferences'

  // Show skeleton during company loading or when no company data or when company state is not ready
  if (isCompanyLoading || !currentCompany || !company) {
    return (
      <div className="space-y-6 p-6 pt-0">
        <PageHeader title={pageHeaderTitle} />

        <Card>
          <CardHeader>
            <CardTitle>Company Details</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-6">
              <div className="max-w-md">
                <Skeleton className="h-4 w-24 mb-2" />
                <Skeleton className="h-10 w-full" />
              </div>
              <div className="max-w-md">
                <Skeleton className="h-4 w-16 mb-2" />
                <Skeleton className="h-10 w-full" />
              </div>
              <div className="space-y-2">
                <Skeleton className="h-4 w-28 mb-2" />
                <div className="space-y-2">
                  {Array.from({ length: 7 }).map((_, i) => (
                    <div key={i} className="flex items-center gap-4">
                      <Skeleton className="h-4 w-16" />
                      <Skeleton className="h-8 w-20" />
                      <Skeleton className="h-4 w-4" />
                      <Skeleton className="h-8 w-20" />
                    </div>
                  ))}
                </div>
              </div>
              <Skeleton className="h-10 w-24" />
            </div>
          </CardContent>
        </Card>
      </div>
    )
  }

  return (
    <div className="space-y-6 p-6 pt-0">
      <PageHeader title="Manage your company details and preferences" />

      <Card>
        <CardHeader>
          <CardTitle>Company Details</CardTitle>
        </CardHeader>
        <CardContent>
          <form onSubmit={handleSubmit} className="space-y-6" noValidate>
            <div className="max-w-md">
              <Label htmlFor="name">Company Name</Label>
              <Input
                id="name"
                value={company?.name || ''}
                onChange={e =>
                  setCompany(prev =>
                    prev ? { ...prev, name: e.target.value } : null,
                  )
                }
                placeholder="Enter company name"
              />
            </div>
            <div className="max-w-md">
              <Label htmlFor="timezone">Timezone</Label>
              <TimezoneSelector
                value={company?.timezoneName || undefined}
                onValueChange={timezone =>
                  setCompany(prev =>
                    prev
                      ? { ...prev, timezoneName: timezone || undefined }
                      : prev,
                  )
                }
                placeholder="Select your company's timezone"
                disabled={saving}
              />
            </div>
            <div className="space-y-2">
              <Label>Business Hours</Label>
              {company?.officeHours ? (
                <WeeklyHoursSchedule
                  value={company.officeHours}
                  onChange={officeHours =>
                    setCompany(prev => (prev ? { ...prev, officeHours } : null))
                  }
                />
              ) : (
                <div className="text-muted-foreground">
                  Loading office hours...
                </div>
              )}
            </div>
            <Button
              type="submit"
              disabled={saving}
              variant="form"
              className="mt-12"
            >
              {saving ? 'Saving...' : 'Save changes'}
            </Button>
          </form>
        </CardContent>
      </Card>
    </div>
  )
}
